import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Flower2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 32, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`hero-line-mask ${className}`}>
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Page = ({ children, className = "" }) => (
  <motion.main
    className={className}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.main>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`eyebrow ${className}`} data-testid="section-eyebrow">
    {children}
  </p>
);

/* Subtle 3D tilt that follows the mouse — premium depth without heaviness */
export const Tilt3D = ({ children, className = "", max = 6 }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [max, -max]), { stiffness: 140, damping: 16 });
  const ry = useSpring(useTransform(mx, [0, 1], [-max, max]), { stiffness: 140, damping: 16 });
  return (
    <motion.div
      className={className}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 950 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      {children}
    </motion.div>
  );
};

/* Drifting floral accents — absolute inside a relative section */
export const Petals = ({ className = "" }) => (
  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
    <Flower2 className="petal absolute left-[6%] top-[16%] h-7 w-7 text-rose/40" style={{ animationDelay: "0s" }} strokeWidth={1.2} />
    <Flower2 className="petal absolute right-[9%] top-[28%] h-5 w-5 text-gold/45" style={{ animationDelay: "1.6s" }} strokeWidth={1.2} />
    <Flower2 className="petal absolute left-[13%] bottom-[14%] h-4 w-4 text-rose/30" style={{ animationDelay: "3s" }} strokeWidth={1.4} />
    <Flower2 className="petal absolute right-[16%] bottom-[22%] h-6 w-6 text-rose/25" style={{ animationDelay: "4.4s" }} strokeWidth={1.2} />
  </div>
);

export { EASE };
